/* eslint-disable react/prop-types */
import { FC, useEffect, useState } from "react";
import { X, Search } from "lucide-react";
import cmsClient from "src/services/cmsClient";

interface AudienceUser {
	cmsUserId: string;
	firstName: string;
	lastName: string;
	department: string;
}

interface Props {
	audienceType: "everyone" | "platform" | "groups" | "users";
	audiencePlatform: "ios" | "android" | "both";
	audienceUserIds: string[];
	onAudienceTypeChange: (
		type: "everyone" | "platform" | "groups" | "users"
	) => void;
	onPlatformChange: (platform: "ios" | "android" | "both") => void;
	onUserIdsChange: (ids: string[]) => void;
}

const AudiencePicker: FC<Props> = ({
	audienceType,
	audiencePlatform,
	audienceUserIds,
	onAudienceTypeChange,
	onPlatformChange,
	onUserIdsChange
}) => {
	const [userSearch, setUserSearch] = useState("");
	const [allUsers, setAllUsers] = useState<AudienceUser[]>([]);
	const [usersError, setUsersError] = useState(false);

	useEffect(() => {
		if (audienceType !== "users") return;
		let cancelled = false;
		setUsersError(false);
		cmsClient
			.get<
				Array<{
					id: string;
					first_name: string;
					last_name: string;
					department: string;
				}>
			>("/announcements/users/")
			.then((res) => {
				if (cancelled) return;
				setAllUsers(
					res.data.map((u) => ({
						cmsUserId: u.id,
						firstName: u.first_name,
						lastName: u.last_name,
						department: u.department ?? ""
					}))
				);
			})
			.catch(() => {
				if (cancelled) return;
				setAllUsers([]);
				setUsersError(true);
			});
		return () => {
			cancelled = true;
		};
	}, [audienceType]);

	const filteredUsers = allUsers.filter(
		(user) =>
			!audienceUserIds.includes(user.cmsUserId) &&
			(`${user.firstName} ${user.lastName}`
				.toLowerCase()
				.includes(userSearch.toLowerCase()) ||
				user.department.toLowerCase().includes(userSearch.toLowerCase()))
	);

	const selectedUsers = allUsers.filter((user) =>
		audienceUserIds.includes(user.cmsUserId)
	);

	const addUser = (cmsUserId: string) => {
		onUserIdsChange([...audienceUserIds, cmsUserId]);
		setUserSearch("");
	};

	const removeUser = (cmsUserId: string) => {
		onUserIdsChange(audienceUserIds.filter((id) => id !== cmsUserId));
	};

	return (
		<div className="flex flex-col gap-4">
			<fieldset>
				<legend className="sr-only">Audience type</legend>
				<div className="flex flex-col gap-2.5">
					{(
						[
							{ value: "everyone", label: "Everyone" },
							{ value: "platform", label: "By Platform" },
							{ value: "groups", label: "By Group" },
							{ value: "users", label: "Specific Users" }
						] as const
					).map((option) => (
						<label
							key={option.value}
							className="flex items-center gap-2.5 cursor-pointer"
						>
							<input
								type="radio"
								name="audienceType"
								value={option.value}
								checked={audienceType === option.value}
								onChange={() => onAudienceTypeChange(option.value)}
								className="h-4 w-4 text-primary border-border focus:ring-primary"
							/>
							<span className="text-sm text-foreground">{option.label}</span>
						</label>
					))}
				</div>
			</fieldset>

			{audienceType === "platform" && (
				<div className="flex rounded-lg border border-border overflow-hidden ml-6">
					{(
						[
							{ value: "ios", label: "iOS" },
							{ value: "android", label: "Android" },
							{ value: "both", label: "Both" }
						] as const
					).map((option) => (
						<button
							key={option.value}
							type="button"
							aria-pressed={audiencePlatform === option.value}
							onClick={() => onPlatformChange(option.value)}
							className={[
								"flex-1 px-3 py-2 text-sm font-medium transition-colors",
								option.value !== "ios" ? "border-l border-border" : "",
								audiencePlatform === option.value
									? "bg-foreground text-white"
									: "bg-white text-muted-foreground hover:bg-muted"
							].join(" ")}
						>
							{option.label}
						</button>
					))}
				</div>
			)}

			{audienceType === "groups" && (
				<div className="ml-6 rounded-lg border border-dashed border-border bg-white px-4 py-6 text-center">
					<p className="text-sm font-medium text-foreground">
						Group targeting coming soon
					</p>
					<p className="text-[13px] text-muted-foreground mt-1">
						Target announcements by department, location, or role
					</p>
				</div>
			)}

			{audienceType === "users" && (
				<div className="ml-6 flex flex-col gap-3">
					{usersError && (
						<p className="text-sm text-red-600">
							Failed to load users. Please try again.
						</p>
					)}
					{selectedUsers.length > 0 && (
						<div className="flex flex-wrap gap-2">
							{selectedUsers.map((user) => (
								<span
									key={user.cmsUserId}
									className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-sm px-3 py-1"
								>
									{user.firstName} {user.lastName}
									<button
										type="button"
										aria-label={`Remove ${user.firstName} ${user.lastName}`}
										onClick={() => removeUser(user.cmsUserId)}
										className="hover:text-primary/70 transition-colors"
									>
										<X className="h-3.5 w-3.5" />
									</button>
								</span>
							))}
						</div>
					)}
					<div className="relative">
						<Search
							size={16}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
						/>
						<input
							type="text"
							value={userSearch}
							onChange={(e) => setUserSearch(e.target.value)}
							placeholder="Search users..."
							className="rounded-lg border border-border bg-white pl-9 pr-4 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-ring"
						/>
					</div>
					{userSearch.length > 0 && filteredUsers.length > 0 && (
						<div className="border border-border rounded-lg bg-white shadow-sm max-h-[200px] overflow-y-auto">
							{filteredUsers.map((user) => (
								<button
									key={user.cmsUserId}
									type="button"
									onClick={() => addUser(user.cmsUserId)}
									className="flex items-center justify-between w-full px-3 py-2 text-sm hover:bg-muted text-left"
								>
									<span className="font-medium text-foreground">
										{user.firstName} {user.lastName}
									</span>
									<span className="text-muted-foreground text-xs">
										{user.department}
									</span>
								</button>
							))}
						</div>
					)}
					{userSearch.length > 0 && filteredUsers.length === 0 && (
						<p className="text-sm text-muted-foreground">No users found</p>
					)}
				</div>
			)}
		</div>
	);
};

export default AudiencePicker;
